"use client"
import React, { useMemo, useState } from 'react'
import HeaderMenu from '@/app/components/HeaderMenu'
import { BlurFade } from '@/app/components/BlurFade'
import { motion, AnimatePresence } from "motion/react"
import { RedirectToMM } from '@/app/components/DeviceRedirect'
import { pick, type Locale } from '@/lib/i18n'
import { solutionsDict } from '@/lib/i18n/dicts/solutions'
import type { SolutionTab } from '@/lib/solutions'
import CtaBanner from '@/app/components/CtaBanner'

interface SolutionsClientProps {
    tabs: SolutionTab[]
    locale: Locale
    // 页脚为服务端组件（读库统计），由服务端页面渲染后传入
    footer: React.ReactNode
}

// 运营方案页：玩法 TAB 切换（步骤流程 + 优势）→ 增长价值卡 → CTA
export default function SolutionsClient({ tabs, locale, footer }: SolutionsClientProps) {
    const t = pick(solutionsDict, locale)
    const [activeId, setActiveId] = useState(tabs[0]?.id ?? 0)
    const active = useMemo(() => tabs.find((x) => x.id === activeId) ?? tabs[0], [tabs, activeId])

    const labelFor = (tab: SolutionTab) => (locale === 'en' && tab.name_en ? tab.name_en : tab.name)
    const stepsOf = (tab: SolutionTab) => (locale === 'en' && tab.steps_en.length > 0 ? tab.steps_en : tab.steps)
    const advantageOf = (tab: SolutionTab) => (locale === 'en' && tab.advantage_en ? tab.advantage_en : tab.advantage)

    return (
        <>
            <RedirectToMM href="/mm/gamelist" />
            <HeaderMenu locale={locale} />

            {/* Hero */}
            <section className="relative overflow-hidden bg-slate-950">
                <div className="pointer-events-none absolute -top-48 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[130px]" />
                <div className="pointer-events-none absolute top-24 right-[-140px] h-[340px] w-[340px] rounded-full bg-cyan-400/10 blur-[110px]" />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-12 text-center">
                    <BlurFade delay={0.05} inView>
                        <span className="inline-flex items-center gap-2 rounded-full ring-1 ring-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-xs font-medium tracking-widest text-indigo-300 uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                            {t.hero.badge}
                        </span>
                    </BlurFade>
                    <BlurFade delay={0.15} inView>
                        <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tight text-white">
                            {t.hero.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shine">
                                {t.hero.titleB}
                            </span>
                        </h1>
                    </BlurFade>
                    <BlurFade delay={0.25} inView>
                        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-400">
                            {t.hero.subtitle}
                        </p>
                    </BlurFade>
                </div>
            </section>

            {/* 玩法 TAB */}
            {/* 玩法 TAB：sticky 偏移与 gamelist2 一致（top-16），避免与顶栏之间露出内容缝隙 */}
            <section className="bg-slate-950 sticky top-16 z-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex flex-wrap justify-center gap-2 py-4 border-b border-white/5">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveId(tab.id)}
                                className={`relative rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${activeId === tab.id ? "text-white" : "text-slate-400 hover:text-slate-200"
                                    }`}
                            >
                                {activeId === tab.id && (
                                    <motion.span
                                        layoutId="solution-pill"
                                        className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 shadow-lg shadow-indigo-600/30"
                                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                                    />
                                )}
                                <span className="relative z-10">{labelFor(tab)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* 玩法详情：步骤流程 + 优势 */}
            <section className="bg-slate-950 pb-16 pt-10 min-h-[420px]">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <AnimatePresence mode="wait">
                        {active && (
                            <motion.div
                                key={active.id}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
                                transition={{ type: "spring", stiffness: 220, damping: 26 }}
                            >
                                <h2 className="text-center text-2xl md:text-3xl font-black text-white">
                                    {labelFor(active)}
                                </h2>

                                {/* 步骤流程 */}
                                <div className="mt-10 flex flex-wrap items-stretch justify-center gap-3">
                                    {stepsOf(active).map((step, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && (
                                                <span className="self-center text-indigo-400/60">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                                    </svg>
                                                </span>
                                            )}
                                            <div className="group relative rounded-2xl bg-slate-900 ring-1 ring-white/10 px-6 py-5 text-center transition-all duration-300 hover:-translate-y-1 hover:ring-indigo-400/50 hover:shadow-xl hover:shadow-indigo-500/10">
                                                <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-sm font-black text-white shadow-lg shadow-indigo-600/30 animate-breathe">
                                                    {i + 1}
                                                </span>
                                                <p className="mt-3 text-sm font-bold text-white">{step}</p>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>

                                {/* 玩法优势 */}
                                {advantageOf(active) && (
                                    <div className="mx-auto mt-10 max-w-3xl">
                                        <div className="rounded-2xl bg-gradient-to-r from-indigo-500/10 via-fuchsia-500/10 to-cyan-400/10 bg-[length:200%_auto] animate-flow-x ring-1 ring-indigo-400/25 px-6 py-4 text-center">
                                            <p className="text-sm font-medium leading-6 text-indigo-200">
                                                {advantageOf(active)}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* 增长价值 */}
            <section className="bg-slate-950 pb-20">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <BlurFade delay={0.05} inView>
                        <h2 className="text-center text-2xl md:text-4xl font-black tracking-tight text-white">
                            {t.section2.titleA}
                            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">{t.section2.titleB}</span>
                        </h2>
                    </BlurFade>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {t.section2.items.map((item, i) => (
                            <BlurFade key={item.title} delay={0.08 * i} inView>
                                <div className="group h-full rounded-2xl bg-slate-900 ring-1 ring-white/10 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:ring-indigo-400/50 hover:shadow-2xl hover:shadow-indigo-500/15">
                                    <span className="text-3xl font-black bg-gradient-to-br from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <h3 className="mt-3 text-lg font-bold text-white group-hover:text-indigo-300 transition-colors duration-300">{item.title}</h3>
                                    <p className="mt-2 text-xs leading-5 text-slate-400">{item.desc}</p>
                                </div>
                            </BlurFade>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA（与首页共用 CtaBanner；本页深色底，需显式补齐外框底色） */}
            <CtaBanner title={t.cta.title} button={t.cta.button} href="../contact2" className="bg-slate-950" />

            {footer}
        </>
    )
}
